import { Dispatch, FC, SetStateAction, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Key } from '@mui/icons-material'
import { Box, Paper, Typography } from '@mui/material'

import { usePOST_resetPasswordMutation, usePOST_validateRestPasswordTokenMutation } from 'redux/api/authApi'

import { Form, Formik } from 'formik'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import InputField from 'components/atoms/fields/InputField'

import { useErrorAlert } from 'utils/customHooks/useErrorAlert'
import { useMedia } from 'utils/customHooks/useMedia'
import { ELEMENT_RADIUS } from 'utils/customizedTheme'
import formModel, { initialValues, validationSchema } from 'utils/formModels/resetPasswordModel'

import logo from 'assets/svg/logo.svg'

import { AuthType } from 'types'
import { useSuccessAlert } from 'utils/customHooks/useSuccessAlert'

interface IProps {
  setAuthState: Dispatch<SetStateAction<AuthType>>
  resetPasswordToken: string
  email: string
}

const RestPasswordForm: FC<IProps> = ({ setAuthState, resetPasswordToken, email }) => {
  const { t } = useTranslation()
  const { isUnder1200px } = useMedia()
  const navigate = useNavigate()

  const { password, confirmPassword } = formModel()

  // Get reset password link on email
  const [POST_validateRestPasswordToken, { error: inValidToken }] =
    usePOST_validateRestPasswordTokenMutation()

  const [POST_resetPassword, { isLoading, isSuccess: resetSuccess, error }] = usePOST_resetPasswordMutation()
  useErrorAlert(error)

  useEffect(() => {
    POST_validateRestPasswordToken({ token: resetPasswordToken })
  }, [])

  useSuccessAlert(
    resetSuccess
      ? `${t('alerts.password-change-success')}`
      : '',
  )

  useEffect(() => {
    if(resetSuccess) navigate('/')
  }, [resetSuccess])

  return (
    <Paper
      className="transparent-background"
      elevation={0}
      sx={{
        borderRadius: ELEMENT_RADIUS,
        overflow: 'hidden',
        marginTop: '-3rem',
        width: isUnder1200px ? '90%' : '30rem',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        component='img'
        sx={{
          height: 'auto',
          width: '10rem',
          margin: '0 auto'
        }}
        alt='EsyBee'
        src={logo}
      />
       <Typography
        variant='h2'
        sx={{ textAlign: 'center', fontSize: '3rem', fontWeight: '200', marginTop: '3rem'}}
      >
        {t('reset.header')}
      </Typography>
      {inValidToken ? (
        <Typography sx={{ fontWeight: '300', margin: '3rem auto 2rem' }}>{t('reset.expired-token')}</Typography>
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            const data = { ...values, token: resetPasswordToken }
            POST_resetPassword(data)
          }}
        >
          <Form>
            <Box sx={{ padding: '1.5rem', minHeight: '10rem' }}>
              <Typography sx={{ fontWeight: '300', margin: '1rem auto' }}>
                {t('reset.enter-new-password')}
              </Typography>
              <InputField
                fieldProps={{ ...password }}
                icon={<Key color='primary' sx={{ mr: 1, my: 0.5 }} />}
                sx={{ marginTop: '1rem' }}
              />
              <InputField
                fieldProps={{ ...confirmPassword }}
                icon={<Key color='primary' sx={{ mr: 1, my: 0.5 }} />}
                sx={{ marginTop: '2rem' }}
              />
              <SubmitButton text={t('common.reset')} sx={{ marginTop: '2rem' }} isLoading={isLoading} />
            </Box>
          </Form>
        </Formik>
      )}
      <Typography
        onClick={() => {
          setAuthState('reset')
          navigate('/login')
        }}
        sx={{
          fontSize: '0.8rem',
          color: '#9fa0a4',
          textAlign: 'center',
          margin: '-0.5rem 0 0.7rem',
          cursor: 'pointer',
          '&:hover': { textDecoration: 'underline', color: 'inherit' },
        }}
      >
        {t('reset.back-to-reset')}
      </Typography>
    </Paper>
  )
}
export default RestPasswordForm
